import { useState, useEffect } from "react";
import {
  MapPin,
  Waves,
  Building2,
  TreePine,
  Dumbbell,
  Trophy,
  Users,
  Baby,
  ChevronDown,
  Phone,
  MessageCircle,
  ArrowRight,
} from "lucide-react";
import { LeadModal } from "../components/LeadModal";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_premium-real-estate-17/artifacts/YOUR_LOGO_PLACEHOLDER";

// Use the exact Tilal Binghatti logo image the user uploaded
const TILAL_LOGO =
  "https://customer-assets.emergentagent.com/job_premium-real-estate-17/artifacts/tilal-logo.png";

const WHATSAPP_LINK =
  "https://wa.me/971567834333?text=Hi%2C%20I%27m%20interested%20in%20Tilal%20Binghatti.%20Please%20share%20the%20brochure%20and%20payment%20plan.";

const HERO_BG =
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=2000&q=80";

const ACCENT = "#1F5C3A"; // deep forest green
const ACCENT_LIGHT = "#3d8158";

const UNITS = [
  { name: "Townhouse (Villa)", type: "villa", br: "4 BR", size: "2,775 sq ft", price: "AED 4,260,000", tag: "Entry" },
  { name: "Grand Villa", type: "grand-villa", br: "5 BR", size: "2,824 sq ft", price: "AED 5,100,000", tag: "Family" },
  { name: "Twin Villa (Mansion)", type: "mansion", br: "6 BR", size: "4,162 sq ft", price: "AED 6,900,000", tag: "Signature" },
  { name: "Standalone (Grand Mansion)", type: "grand-mansion", br: "6–7 BR", size: "6,814 sq ft", price: "AED 16,270,000", tag: "Ultra Luxury" },
];

const AMENITIES = [
  { icon: Waves, num: "01", title: "Private Beach", desc: "A 12,000 sqm man-made beach at the community's heart, framed by cabanas and palm-lined boardwalks." },
  { icon: Waves, num: "02", title: "Swimmable Lakes", desc: "A network of crystal-clear, swimmable lakes threaded through the villa clusters." },
  { icon: TreePine, num: "03", title: "Landscaped Parks", desc: "Green corridors, pet-friendly walkways and shaded gardens connecting every block." },
  { icon: Dumbbell, num: "04", title: "Wellness & Fitness", desc: "State-of-the-art fitness centres, spa lounges and dedicated wellness pavilions." },
  { icon: Trophy, num: "05", title: "Sports & Recreation", desc: "Multi-sport courts, professional cycling and jogging tracks throughout the masterplan." },
  { icon: Users, num: "06", title: "Clubhouse & Retail", desc: "Community clubhouse, curated retail boulevard and signature dining destinations." },
  { icon: Baby, num: "07", title: "Family & Kids", desc: "Themed play zones, an on-site nursery, kids' clubs and family gathering spaces." },
  { icon: Building2, num: "50+", title: "Total Amenities", desc: "Clinics, mosque, pet parks, 24/7 concierge security and smart-home infrastructure." },
];

const LOCATIONS = [
  { place: "Dubai Creek Harbour", time: "≈ 15 min" },
  { place: "Meydan Racecourse", time: "≈ 18 min" },
  { place: "Burj Khalifa & Dubai Mall", time: "≈ 20 min" },
  { place: "Dubai International Airport", time: "≈ 25 min" },
  { place: "E611 Emirates Road", time: "Direct access" },
  { place: "E66 Dubai–Al Ain Road", time: "Direct access" },
  { place: "Blue Line Metro (planned)", time: "Academic City stn." },
];

const FAQ = [
  {
    q: "What types of homes are available at Tilal Binghatti?",
    a: "A curated collection of 4- and 5-bedroom townhouses, 6-bedroom twin villas and larger standalone mansions — each designed in Binghatti's signature architectural language, adapted for horizontal, garden-facing living.",
  },
  {
    q: "Why does this stand out as an investment?",
    a: "Tilal Binghatti is Binghatti's first master-planned villa community. It combines an emerging premium location, resort-style amenities rarely seen in Dubai's villa segment, and a proven developer track record — supporting both long-term appreciation and strong rental demand.",
  },
  {
    q: "When is handover expected?",
    a: "Current developer guidance indicates a 2028–2029 handover window. The exact date is confirmed at time of booking — we'll share the latest developer update during your consultation.",
  },
  {
    q: "What are the payment terms?",
    a: "A construction-linked payment plan is available, typically starting with a booking deposit followed by staged milestones. Full terms are confirmed by the developer at booking — request the current plan and we'll share it immediately.",
  },
  {
    q: "How do I proceed with a booking?",
    a: "Submit the interest form or WhatsApp our consultants. We'll walk you through unit selection, share the brochure and floor plans, and coordinate the reservation and booking process end-to-end.",
  },
];

const WhatsAppIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.33 4.95L2.05 22l5.25-1.38a9.9 9.9 0 0 0 4.74 1.2h.01c5.46 0 9.91-4.45 9.91-9.91C21.96 6.45 17.5 2 12.04 2zm0 18.02h-.01a8.2 8.2 0 0 1-4.18-1.14l-.3-.18-3.12.82.83-3.04-.2-.31a8.2 8.2 0 0 1-1.26-4.35c0-4.53 3.7-8.22 8.24-8.22 2.2 0 4.27.86 5.83 2.42a8.18 8.18 0 0 1 2.41 5.81c0 4.53-3.7 8.19-8.24 8.19zm4.52-6.16c-.25-.12-1.47-.72-1.7-.81-.23-.08-.4-.12-.56.13-.17.25-.64.81-.79.97-.14.17-.29.19-.54.06-.25-.12-1.04-.38-1.98-1.22-.73-.65-1.23-1.46-1.37-1.71-.14-.25-.02-.38.11-.51.11-.11.25-.29.37-.43.12-.15.16-.25.24-.42.08-.17.04-.31-.02-.44-.06-.12-.56-1.35-.77-1.85-.2-.48-.41-.42-.56-.43-.14-.01-.31-.01-.48-.01-.17 0-.44.06-.67.31-.23.25-.87.85-.87 2.08 0 1.22.89 2.41 1.01 2.58.12.17 1.75 2.67 4.24 3.74.59.26 1.05.41 1.41.52.59.19 1.13.16 1.56.1.48-.07 1.47-.6 1.67-1.18.21-.58.21-1.08.15-1.18-.06-.1-.23-.16-.48-.28z" />
  </svg>
);

export default function TilalBinghatti() {
  const [modalOpen, setModalOpen] = useState(false);
  const [prefillUnit, setPrefillUnit] = useState("");
  const [openFaq, setOpenFaq] = useState(0);

  // Auto-open the interest popup for new visitors
  useEffect(() => {
    if (sessionStorage.getItem("tilal_modal_shown") === "1") return;
    const t = setTimeout(() => {
      setModalOpen(true);
      sessionStorage.setItem("tilal_modal_shown", "1");
    }, 3500);
    return () => clearTimeout(t);
  }, []);

  const openModal = (unit = "") => {
    setPrefillUnit(unit);
    setModalOpen(true);
  };

  return (
    <div className="tilal-page min-h-screen bg-[#061510] text-white" data-testid="tilal-binghatti-page">
      {/* NAVIGATION */}
      <nav className="fixed top-0 left-0 right-0 z-40" style={{ background: "rgba(6,21,16,0.85)", backdropFilter: "blur(14px)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a href="#top" className="flex items-center gap-3" data-testid="tilal-nav-logo">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: ACCENT }}>
              <span className="font-serif-display italic text-white text-lg">T</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-display italic text-lg">Tilal</div>
              <div className="text-[9px] tracking-[0.4em] text-white/60 uppercase">Binghatti</div>
            </div>
          </a>

          <div className="hidden md:flex items-center gap-8 text-[11px] tracking-[0.25em] uppercase text-white/70">
            <a href="#overview" className="hover:text-white transition">Overview</a>
            <a href="#location" className="hover:text-white transition">Location</a>
            <a href="#units" className="hover:text-white transition">Homes</a>
            <a href="#amenities" className="hover:text-white transition">Amenities</a>
            <a href="#faq" className="hover:text-white transition">FAQ</a>
          </div>

          <div className="flex items-center gap-3">
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-[11px] font-bold tracking-[0.2em] text-white transition hover:opacity-90"
              style={{ background: "#25D366" }}
              data-testid="tilal-nav-whatsapp"
            >
              <WhatsAppIcon size={14} /> WHATSAPP
            </a>
            <button
              onClick={() => openModal()}
              className="pulse-cta-soft px-5 py-2.5 rounded-full text-[11px] font-bold tracking-[0.2em] text-white transition hover:opacity-90"
              style={{ background: ACCENT }}
              data-testid="tilal-nav-register"
            >
              REGISTER INTEREST
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section id="top" className="relative min-h-screen flex items-center overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(ellipse at center, rgba(31,92,58,0.35) 0%, rgba(6,21,16,0.85) 60%, rgba(6,21,16,0.98) 100%)`,
          }}
        />
        <div
          className="absolute inset-0 opacity-40"
          style={{ background: `linear-gradient(180deg, transparent 0%, ${ACCENT}20 100%)` }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-32 pb-20 w-full">
          <div className="max-w-4xl mx-auto text-center">
            {/* Logo card */}
            <div className="inline-block mb-10 relative">
              <div className="w-40 h-40 md:w-48 md:h-48 mx-auto rounded-2xl flex flex-col items-center justify-center relative overflow-hidden shadow-2xl" style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)`, border: "1px solid rgba(255,255,255,0.15)" }}>
                <div className="relative z-10 text-center px-4">
                  <div className="font-serif-display text-white text-5xl md:text-6xl leading-none">
                    Tilal
                  </div>
                  <div className="w-16 h-px bg-white/40 mx-auto my-3" />
                  <div className="text-[10px] md:text-xs tracking-[0.5em] text-white/95 uppercase">Binghatti</div>
                </div>
                <div className="absolute inset-0 opacity-20" style={{
                  background: "radial-gradient(circle at 30% 20%, rgba(255,255,255,0.4), transparent 60%)"
                }} />
              </div>
            </div>

            <div className="text-[10px] md:text-xs tracking-[0.5em] uppercase mb-6" style={{ color: "#a8d5b9" }}>
              Academic City, Dubai · Binghatti Developers
            </div>
            <h1 className="font-serif-display text-4xl md:text-6xl lg:text-7xl font-light leading-[1.05] mb-6">
              A private village of villas,<br />
              <em className="italic" style={{ color: "#a8d5b9" }}>reimagined by Binghatti</em>
            </h1>
            <p className="text-base md:text-lg text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              Binghatti's inaugural master-planned villa community — over 10 million square feet
              of private residences woven around swimmable lakes, a private beach and fifty-plus
              lifestyle amenities. Refined. Considered. Rare.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => openModal()}
                className="pulse-cta inline-flex items-center justify-center gap-3 px-8 py-4 rounded-lg text-sm font-bold tracking-[0.25em] text-white transition hover:-translate-y-0.5"
                style={{ background: ACCENT }}
                data-testid="hero-register-btn"
              >
                REGISTER YOUR INTEREST <ArrowRight size={16} />
              </button>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-lg text-sm font-bold tracking-[0.25em] text-white transition hover:opacity-90 hover:-translate-y-0.5"
                style={{ background: "#25D366", boxShadow: "0 10px 30px rgba(37,211,102,0.4)" }}
                data-testid="hero-whatsapp-btn"
              >
                <WhatsAppIcon /> CHAT ON WHATSAPP
              </a>
            </div>

            {/* Stats */}
            <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden max-w-5xl mx-auto">
              {[
                { v: "AED 4.2M", l: "Starting from" },
                { v: "4–7 BR", l: "Villas & Townhouses" },
                { v: "10M+", l: "Sq Ft Masterplan" },
                { v: "50+", l: "Lifestyle Amenities" },
              ].map((s) => (
                <div key={s.l} className="bg-[#061510] px-4 py-6">
                  <div className="font-serif-display text-2xl md:text-3xl" style={{ color: "#a8d5b9" }}>{s.v}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-white/50 mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <a href="#overview" className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 hover:text-white flex flex-col items-center gap-2 animate-bounce">
          <span className="text-[10px] tracking-[0.4em]">EXPLORE</span>
          <ChevronDown size={18} />
        </a>
      </section>

      {/* OVERVIEW */}
      <section id="overview" className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>01 · The Project</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-8">
              Binghatti's first <em className="italic">ground-level</em> community
            </h2>
            <div className="space-y-5 text-white/70 leading-relaxed">
              <p className="text-lg text-white/85">
                For over a decade, Binghatti has redefined Dubai's skyline through boldly angular,
                parametric towers. Tilal Binghatti marks a deliberate — and remarkable — departure.
              </p>
              <p>
                This is the developer's inaugural low-rise, master-planned villa and townhouse
                community: a self-contained residential district in Academic City, conceived not as a
                single tower but as an entire neighbourhood. The 10-million square foot masterplan
                is organised around water and greenery rather than height.
              </p>
              <p>
                A private man-made beach, a network of swimmable lakes, landscaped parks and
                wellness corridors sit alongside a retail boulevard, sports pavilions and community
                facilities — a fully walkable enclave built from the ground up for refined family
                living.
              </p>
              <p>
                Residents choose from four- and five-bedroom townhouses, six-bedroom twin villas
                and seven-bedroom standalone mansions, each carrying Binghatti's signature
                architectural language reinterpreted for horizontal, garden-facing homes.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5 space-y-4">
            {[
              { k: "Developer", v: "Binghatti Developers" },
              { k: "Location", v: "Academic City, Dubai" },
              { k: "Property Types", v: "Townhouses & Villas · 4–7 BR" },
              { k: "Masterplan", v: "10 million sq ft · 50+ amenities" },
              { k: "Handover", v: "Est. 2028–2029" },
              { k: "Payment Plan", v: "Construction-linked · flexible" },
            ].map((f) => (
              <div key={f.k} className="rounded-xl p-5 border" style={{ background: "rgba(31,92,58,0.08)", borderColor: `${ACCENT}30` }}>
                <div className="text-[10px] tracking-[0.35em] uppercase" style={{ color: "#a8d5b9" }}>{f.k}</div>
                <div className="font-serif-display text-xl mt-2">{f.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCATION */}
      <section id="location" className="py-24 md:py-32 px-6 md:px-10" style={{ background: "rgba(31,92,58,0.06)", borderTop: "1px solid rgba(255,255,255,0.05)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>02 · Location</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-6">
              Connected, without <em className="italic">giving up quiet</em>
            </h2>
            <p className="text-white/60 leading-relaxed">
              Academic City sits along Dubai's principal east–west corridors, offering direct
              access to Emirates Road and Dubai–Al Ain Road, with a Blue Line metro station
              planned within walking distance.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <div className="rounded-2xl overflow-hidden border" style={{ borderColor: `${ACCENT}30` }}>
              <ul className="divide-y divide-white/5">
                {LOCATIONS.map((l) => (
                  <li key={l.place} className="flex items-center justify-between px-6 py-4 hover:bg-white/[0.02] transition">
                    <span className="flex items-center gap-3 text-white/85">
                      <MapPin size={14} style={{ color: "#a8d5b9" }} /> {l.place}
                    </span>
                    <span className="text-white/50 text-sm font-medium">{l.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border aspect-square lg:aspect-auto" style={{ borderColor: `${ACCENT}30` }}>
              <iframe
                title="Academic City, Dubai"
                src="https://www.google.com/maps?q=Academic+City+Dubai&output=embed"
                className="w-full h-full min-h-[400px]"
                style={{ border: 0, filter: "grayscale(0.3) contrast(1.1)" }}
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* UNITS */}
      <section id="units" className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>03 · Homes</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-6">
              Four ways to <em className="italic">live at Tilal</em>
            </h2>
            <p className="text-white/60 leading-relaxed">
              Indicative starting prices below. Exact availability, floor plans and final pricing
              are confirmed at time of registration — request the full price list.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {UNITS.map((u, i) => (
              <div
                key={u.type}
                className="rounded-2xl p-8 border transition hover:-translate-y-1 hover:shadow-2xl"
                style={{ background: "rgba(255,255,255,0.02)", borderColor: `${ACCENT}30` }}
                data-testid={`unit-card-${u.type}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <div className="text-[10px] tracking-[0.35em] uppercase mb-2" style={{ color: "#a8d5b9" }}>
                      {String(i + 1).padStart(2, "0")} · {u.tag}
                    </div>
                    <h3 className="font-serif-display text-2xl md:text-3xl">{u.name}</h3>
                    <div className="text-white/50 text-sm mt-1">{u.br}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[9px] tracking-[0.3em] uppercase text-white/40 mb-1">From</div>
                    <div className="font-serif-display text-xl" style={{ color: "#a8d5b9" }}>{u.price}</div>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-6 border-t border-white/5 text-center mb-6">
                  <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/40">Bedrooms</div>
                    <div className="text-sm mt-1 font-medium">{u.br}</div>
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/40">Size from</div>
                    <div className="text-sm mt-1 font-medium">{u.size}</div>
                  </div>
                  <div>
                    <div className="text-[9px] tracking-[0.25em] uppercase text-white/40">Handover</div>
                    <div className="text-sm mt-1 font-medium">2028–29</div>
                  </div>
                </div>

                <button
                  onClick={() => openModal(`${u.name} — ${u.br}`)}
                  className="w-full py-3 rounded-lg text-[11px] font-bold tracking-[0.3em] transition hover:opacity-90 flex items-center justify-center gap-2"
                  style={{ background: ACCENT, color: "#fff" }}
                  data-testid={`unit-enquire-${u.type}`}
                >
                  ENQUIRE <ArrowRight size={13} />
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AMENITIES */}
      <section id="amenities" className="py-24 md:py-32 px-6 md:px-10" style={{ background: "rgba(31,92,58,0.06)" }}>
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-14">
            <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>04 · Amenities</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-6">
              Over 50 amenities, <em className="italic">all within walking distance</em>
            </h2>
            <p className="text-white/60 leading-relaxed">
              A short selection of what's planned across the masterplan. The community is
              designed so that daily life rarely requires a car.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {AMENITIES.map((a) => {
              const Icon = a.icon;
              return (
                <div
                  key={a.title}
                  className="rounded-2xl p-6 border transition hover:-translate-y-1"
                  style={{ background: "rgba(6,21,16,0.6)", borderColor: `${ACCENT}30` }}
                  data-testid={`amenity-${a.num}`}
                >
                  <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4" style={{ background: `${ACCENT}25` }}>
                    <Icon size={18} style={{ color: "#a8d5b9" }} strokeWidth={1.5} />
                  </div>
                  <div className="text-[10px] tracking-[0.35em] uppercase mb-1" style={{ color: "#a8d5b9" }}>{a.num}</div>
                  <h3 className="font-serif-display text-xl mb-2">{a.title}</h3>
                  <p className="text-white/60 text-sm leading-relaxed">{a.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INVEST */}
      <section className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-7xl mx-auto text-center">
          <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>05 · Why Invest</div>
          <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-16 max-w-3xl mx-auto">
            Backed by Dubai's <em className="italic">fastest-growing developer</em>
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { v: "7–10%", l: "Projected rental yield range" },
              { v: "30+", l: "Active Binghatti projects" },
              { v: "20,000+", l: "Units delivered & in development" },
              { v: "#1", l: "Top-selling off-plan developer, 2025" },
            ].map((s) => (
              <div key={s.l} className="rounded-2xl p-8 border text-center" style={{ background: "rgba(255,255,255,0.02)", borderColor: `${ACCENT}30` }}>
                <div className="font-serif-display text-4xl md:text-5xl mb-3" style={{ color: "#a8d5b9" }}>{s.v}</div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-white/50">{s.l}</div>
              </div>
            ))}
          </div>

          <p className="text-xs text-white/40 mt-10 max-w-3xl mx-auto leading-relaxed">
            Figures reflect publicly reported developer performance and third-party market estimates
            at the time of writing; not a guarantee of future returns. Rental yields vary by unit
            type, market conditions and management.
          </p>
        </div>
      </section>

      {/* LEAD CTA STRIP */}
      <section className="py-20 px-6 md:px-10" style={{ background: `linear-gradient(135deg, ${ACCENT} 0%, ${ACCENT_LIGHT} 100%)` }}>
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight mb-6">
            Ready to move forward?
          </h2>
          <p className="text-white/85 mb-10 max-w-2xl mx-auto leading-relaxed">
            Speak to a senior property consultant. Receive the full brochure, floor plans and
            current payment plan — confidentially, within one business day.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => openModal()}
              className="pulse-cta-soft inline-flex items-center justify-center gap-3 px-8 py-4 rounded-lg text-sm font-bold tracking-[0.25em] text-[#0a2e1c] bg-white hover:bg-white/90 transition"
              data-testid="cta-strip-register"
            >
              REGISTER YOUR INTEREST <ArrowRight size={16} />
            </button>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-lg text-sm font-bold tracking-[0.25em] text-white bg-black/30 hover:bg-black/50 transition"
            >
              <WhatsAppIcon /> WHATSAPP US
            </a>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 md:py-32 px-6 md:px-10">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12 text-center">
            <div className="text-[10px] tracking-[0.5em] uppercase mb-4" style={{ color: "#a8d5b9" }}>06 · FAQ</div>
            <h2 className="font-serif-display text-3xl md:text-5xl font-light leading-tight">
              Common <em className="italic">questions</em>
            </h2>
          </div>

          <div className="space-y-3">
            {FAQ.map((f, i) => (
              <div
                key={i}
                className="rounded-xl border overflow-hidden transition"
                style={{ background: "rgba(255,255,255,0.02)", borderColor: `${ACCENT}30` }}
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between text-left px-6 py-5 hover:bg-white/[0.02]"
                >
                  <span className="font-serif-display text-lg md:text-xl pr-6">{f.q}</span>
                  <span
                    className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-lg transition-transform"
                    style={{
                      background: `${ACCENT}30`,
                      transform: openFaq === i ? "rotate(45deg)" : "none",
                    }}
                  >
                    +
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-white/65 leading-relaxed text-sm md:text-base">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-14 px-6 md:px-10 border-t border-white/5" style={{ background: "#040f0b" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: ACCENT }}>
              <span className="font-serif-display italic text-white">T</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-display italic text-base">Tilal Binghatti</div>
              <div className="text-[9px] tracking-[0.35em] uppercase text-white/40">Academic City · Dubai</div>
            </div>
          </div>

          <div className="flex items-center gap-4 text-sm">
            <a href="tel:+971567834333" className="flex items-center gap-2 text-white/70 hover:text-white transition" data-testid="footer-phone">
              <Phone size={14} /> +971 56 783 4333
            </a>
            <a href={WHATSAPP_LINK} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:opacity-80 transition" style={{ color: "#25D366" }} data-testid="footer-whatsapp">
              <MessageCircle size={14} /> WhatsApp
            </a>
          </div>

          <div className="text-[10px] tracking-[0.3em] uppercase text-white/35 text-center md:text-right">
            © 2026 Tilal Binghatti Sales · Marketing partner<br />
            Prices &amp; details subject to developer confirmation
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp */}
      <a
        href={WHATSAPP_LINK}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-6 right-6 z-40 w-16 h-16 rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-transform"
        style={{ background: "#25D366", boxShadow: "0 10px 40px rgba(37,211,102,0.5)" }}
        aria-label="WhatsApp"
        data-testid="tilal-floating-whatsapp"
      >
        <WhatsAppIcon size={28} />
        <span className="absolute inset-0 rounded-full animate-ping" style={{ background: "#25D366", opacity: 0.4 }} />
      </a>

      <LeadModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        projectName="Tilal Binghatti"
        prefillUnit={prefillUnit}
        accentColor={ACCENT}
      />
    </div>
  );
}
